import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import EmptyState from "./EmptyState";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const ClarityScoreChart = ({ data, height = 360 }) => {
  // Check if we have actual data
  if (!data || data.length === 0) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle flex flex-col" style={{ height }}>
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Clarity score</h3>
          <TooltipUI
            content="Daily clarity score (0–100) indicating how clear and actionable messages are."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <EmptyState
          variant="no-data"
          title="No clarity scores available"
          description="There's no clarity score data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  // Format date for display (e.g., "Jan 1" or "Feb 15")
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return `${date.toLocaleString('default', { month: 'short' })} ${day}`;
  };

  // Calculate average clarity score
  const calculateAverage = () => {
    if (!data || data.length === 0) return 0;
    const sum = data.reduce((total, item) => total + item.score, 0);
    return (sum / data.length).toFixed(0); // Round to whole number for percentage
  };

  // Get clarity level based on score
  const getClarityLevel = (score) => {
    if (score >= 80) return "Very clear";
    if (score >= 60) return "Clear";
    if (score >= 40) return "Moderate";
    if (score >= 20) return "Unclear";
    return "Very unclear";
  };

  // Get clarity level badge color
  const getClarityBadgeColor = (level) => {
    switch (level.toLowerCase()) {
      case 'very clear':
      case 'clear':
        return 'bg-[#DAFBEB] text-black-0';
      case 'moderate':
        return 'bg-[#FBF0DA] text-black-0';
      case 'unclear':
      case 'very unclear':
        return 'bg-[#FBDADA] text-black-0';
      default:
        return 'bg-grey-20 text-grey-60';
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const [year, month, day] = label.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const formattedDate = date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric'
      });
      
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium">{formattedDate}</p>
          <p className="text-sm text-grey-60">
            Clarity: <span className="font-medium text-black-0">{payload[0].value}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white-f p-5 pl-0 rounded-lg border border-grey-20 shadow-subtle flex flex-col" style={{ height }}>
      <div className="pl-5 mb-4 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Clarity score</h3>
          <TooltipUI
            content="Daily clarity score (0–100) indicating how clear and actionable messages are."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <div className="flex items-center gap-2">
          <p className="font-medium text-[18px] text-black-0">
            {calculateAverage()}%
          </p>
          <span className={`px-2 py-1 rounded-md text-xs font-normal ${getClarityBadgeColor(getClarityLevel(calculateAverage()))}`}>
            {getClarityLevel(calculateAverage())}
          </span>
        </div>
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate} 
              tick={{ fontSize: 12 }}
              tickMargin={10}
              stroke="#ABABBA"
            />
            <YAxis 
              domain={[0, 100]} 
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 12 }}
              tickMargin={10}
              stroke="#ABABBA"
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={50} stroke="#ABABBA" strokeDasharray="3 3" />
            <Line
              type="linear"
              dataKey="score"
              stroke="#31048B"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 5, fill: "#31048B" }}
              isAnimationActive={true}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ClarityScoreChart; 